// RoutingStrip chips (components/RoutingStrip.jsx, as on the SO page) from a job's tasks, grouped by
// utils/groupJobTasks.js. Layout only: used by the phone queue's task sheet, the phone job detail and the
// phone order screen (whose job strips also show the desktop Production Detail's outside-vendor states).
import { groupJobTasks, stepLabel } from "../../utils/groupJobTasks";
import { outsideChip } from "../../features/salesOrders/lineSteps";

/** A task / step status as a chip state. */
export function stepState(status, blocked) {
  if (status === "Completed") return "done";
  if (status === "Cancelled" || status === "Voided") return "cancelled";
  if (status === "In Progress" || status === "Paused" || status === "Partial") return "active";
  if (blocked) return "blocked";
  return "pending";
}

const mark = (ids, here) => (here != null && ids.includes(here) ? "this task" : undefined);

function sharedChips(shared, here) {
  return shared.map((t) => ({ key: `shared-${t.task_id}`, label: stepLabel(t), sub: mark([t.task_id], here), state: stepState(t.status, t.blocked) }));
}

function partChips(group, here, outside) {
  return group.steps.map((s) => {
    const chip = {
      key: s.key + (s.taskIds || []).join(","),
      label: s.label,
      sub: mark(s.taskIds || [], here),
      state: stepState(s.status, s.blocked),
      sets: s.sets ? { done: s.setsDone, total: s.sets } : undefined,
      defects: s.defect_quantity || undefined,
    };
    const os = outside ? outsideChip(s, outside) : null;
    return os && os.state ? { ...chip, state: os.state, sub: os.sub } : chip;
  });
}

/** One task's routing: the shared sheet / list first, then its part's chain ("this task" marked). */
export function taskRoutingSteps(allTasks, decoupledDefects, task) {
  const grouped = groupJobTasks({ tasks: allTasks, decoupled_defects: decoupledDefects || [] });
  const group = task.part_id != null ? grouped.groups.find((g) => g.part_id === task.part_id) : null;
  return sharedChips(grouped.shared, task.task_id).concat(group ? partChips(group, task.task_id) : []);
}

/**
 * A whole job's routing: the shared sheet / list chips, then one strip per part.
 * `outside` = { today, shownSoId } (the order screen): a step at an outside vendor reads as on the desktop's
 * Production Detail (lineSteps.outsideChip: "420 out · Alexander MFG · due 10/23", overdue, on a draft,
 * "for SO-1131"). Without it the chips show the task states only.
 */
export function jobRouting(allTasks, decoupledDefects, outside = null) {
  const grouped = groupJobTasks({ tasks: allTasks || [], decoupled_defects: decoupledDefects || [] });
  return {
    shared: sharedChips(grouped.shared, null),
    parts: grouped.groups
      .filter((g) => g.steps.length > 0)
      .map((g) => ({ partId: g.part_id, productId: g.product_id, description: g.description, steps: partChips(g, null, outside) })),
  };
}
